import { useTranslate } from "@tolgee/react";
import { DepositDialog } from "#/features/trade/components/order-form/deposit-dialog.tsx";
import { OrderSubmitButton } from "#/features/trade/components/order-form/order-submit-button.tsx";
import { TradePanel } from "../trade-panel.tsx";
import { useOrderFormSetup } from "./hooks/use-order-form-setup.ts";
import { OrderAccountSummary } from "./order-account-summary.tsx";
import { OrderFormFields } from "./order-form-fields.tsx";
import { OrderFormMetrics } from "./order-form-metrics.tsx";
import { OrderFormOptions } from "./order-form-options.tsx";
import { OrderFormToolbar } from "./order-form-toolbar.tsx";
import { OrderSideToggle } from "./order-side-toggle.tsx";

export function OrderForm() {
	const { t } = useTranslate();
	const {
		wallet,
		marketData,
		form,
		quickPrice,
		marginCapability,
		updateLeverage,
		depositAction,
		placeOrder,
	} = useOrderFormSetup();

	return (
		<TradePanel className="overflow-hidden shadow-none">
			<div className="flex min-h-0 flex-1 flex-col gap-2 p-3.5">
				<OrderFormToolbar
					coin={marketData.coin}
					marginMode={form.marginMode}
					onMarginModeChange={form.setMarginMode}
					leverageMarks={form.leverageMarks}
					selectedLeverage={form.selectedLeverage}
					selectedLeverageIndex={form.selectedLeverageIndex}
					onLeverageChange={form.setSelectedLeverageIndex}
					maxLeverage={marketData.maxLeverage}
					hasAvailableFunds={form.hasAvailableFunds}
					orderType={form.orderType}
					onOrderTypeChange={form.setOrderType}
					updateLeverage={updateLeverage}
					marginCapability={marginCapability}
				/>

				<OrderSideToggle side={form.side} onChange={form.setSide} />

				<OrderAccountSummary
					availableFunds={form.availableFunds}
					currentPositionLabel={form.currentPositionLabel}
				/>

				<OrderFormFields
					form={form}
					coin={marketData.coin}
					szDecimals={marketData.szDecimals ?? 4}
					quickPrice={quickPrice}
				/>

				<OrderFormMetrics
					estimatedLiquidation={form.estimatedLiquidation}
					marginRequired={form.marginRequired}
				/>

				<OrderFormOptions
					reduceOnly={form.reduceOnly}
					onReduceOnlyChange={form.setReduceOnly}
					autoQuoteAvailable={form.autoQuoteAvailable}
					showAutoQuote={form.showLimitPrice}
					autoQuote={form.autoQuote}
					onAutoQuoteChange={form.setAutoQuote}
					showTimeInForce={form.showTimeInForce}
					timeInForce={form.timeInForce}
					onTimeInForceChange={form.setTimeInForce}
				/>

				{wallet.isConnected && !form.hasAvailableFunds ? (
					<DepositDialog
						onConfirm={(amount) => depositAction.mutateAsync(amount)}
					>
						<button
							type="button"
							className="mt-auto flex min-h-10.5 w-full items-center justify-center rounded-[9px] bg-primary/10 text-label1-bold text-primary transition-colors hover:bg-primary/16"
						>
							{t("form.deposit-to-trade")}
						</button>
					</DepositDialog>
				) : (
					<OrderSubmitButton
						submitLabel={
							form.aboveMaxNotional
								? t("validation.max-order")
								: form.belowMinNotional
									? t("validation.min-order")
									: form.submitLabel
						}
						side={form.side}
						canSubmit={form.canSubmit}
						onSubmit={() => {
							placeOrder.mutate(form.orderParams);
							form.resetInputs();
						}}
					/>
				)}
			</div>
		</TradePanel>
	);
}
